import { useEffect, useState } from "react";
import { api, STATUSES, statusMeta } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { TrendingUp, Users, DollarSign, Megaphone, Plus, RotateCcw, Trophy, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import LeadFormDialog from "@/components/LeadFormDialog";
import BusinessPulseCard from "@/components/BusinessPulseCard";
import BusinessCoachWidget from "@/components/BusinessCoachWidget";
import UnreadCommsCard from "@/components/UnreadCommsCard";
import UnansweredCommsCard from "@/components/UnansweredCommsCard";
import { ExpenseReviewCard } from "@/components/ExpenseReviewCard";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#FF5A00]" />
        </div>
        <span className="text-xs uppercase font-mono-accent tracking-wider text-zinc-500">{label}</span>
      </div>
      <div className="font-heading text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState({});
  const nav = useNavigate();

  const load = async () => {
    const [s, l, c, seg] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/leads"),
      api.get("/campaigns"),
      api.get("/segments/counts"),
    ]);
    setStats(s.data);
    setRecent(l.data.slice(0, 6));
    setCampaigns(c.data);
    setSegments(seg.data);
  };

  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      const { data } = await api.post("/leads", payload);
      toast.success("Lead created");
      setOpen(false);
      await load();
      nav(`/app/leads/${data.id}`);
    } catch (e) {
      toast.error("Failed to create lead");
    }
  };

  return (
    <div className="p-8 max-w-7xl">
      <BusinessPulseCard />
      <UnansweredCommsCard />
      <ExpenseReviewCard />
      <UnreadCommsCard />
      <BusinessCoachWidget />
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Dashboard</div>
          <h1 className="font-heading text-4xl font-bold">Overview</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="overview-new-lead-btn">
          <Plus className="w-4 h-4 mr-1"/> New lead
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Users} label="Total leads" value={stats?.total_leads ?? "—"} />
        <Stat icon={TrendingUp} label="In pipeline" value={stats ? (stats.total_leads - (stats.by_status?.completed || 0) - (stats.by_status?.lost || 0)) : "—"} />
        <Stat icon={DollarSign} label="Pipeline value" value={stats ? `$${(stats.total_pipeline_value||0).toLocaleString()}` : "—"} sub={stats ? `Booked $${(stats.booked_value||0).toLocaleString()}` : ""} />
        <Stat icon={Megaphone} label="Campaigns" value={stats?.campaigns_count ?? "—"} />
      </div>

      {/* Segment breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="segment-breakdown">
        <SegmentCard
          testid="segment-card-booked"
          icon={Trophy}
          accent="emerald"
          label="Booked"
          value={segments.booked || 0}
          sub="Active jobs to fulfill"
          cta="View booked leads"
          onCta={() => nav("/app/leads")}
        />
        <SegmentCard
          testid="segment-card-completed"
          icon={CheckCircle2}
          accent="zinc"
          label="Completed"
          value={segments.completed || 0}
          sub="Ready for review requests"
          cta="Send review-request campaign"
          onCta={() => nav("/app/campaigns")}
        />
        <SegmentCard
          testid="segment-card-lost"
          icon={RotateCcw}
          accent="indigo"
          label="Lost"
          value={segments.lost || 0}
          sub="Reactivation opportunities"
          cta="Launch reactivation"
          onCta={() => nav("/app/campaigns")}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold">Recent leads</h2>
            <button onClick={()=>nav("/app/leads")} className="text-sm text-[#FF5A00] hover:underline" data-testid="overview-view-all-leads">View all →</button>
          </div>
          {recent.length === 0 ? (
            <div className="text-zinc-500 text-sm py-8 text-center">No leads yet. Create your first one.</div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {recent.map((l) => {
                const s = statusMeta(l.status);
                return (
                  <button key={l.id} onClick={()=>nav(`/app/leads/${l.id}`)} className="w-full flex items-center justify-between py-3 text-left hover:bg-zinc-50 px-2 rounded transition-colors" data-testid={`overview-lead-row-${l.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{l.customer_name}</div>
                      <div className="text-xs text-zinc-500 truncate">{l.service_requested || "—"}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono-accent text-sm text-zinc-700">${(l.estimated_value||0).toLocaleString()}</span>
                      <span className={`px-2 py-1 text-xs border rounded-md ${s.color}`}>{s.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
          <h2 className="font-heading text-xl font-bold mb-4">Pipeline breakdown</h2>
          <div className="space-y-2">
            {STATUSES.map((s) => {
              const count = stats?.by_status?.[s.key] ?? 0;
              const total = stats?.total_leads || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-zinc-700">{s.label}</span>
                    <span className="font-mono-accent text-zinc-900">{count}</span>
                  </div>
                  <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF5A00] transition-all" style={{ width: `${pct}%` }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <LeadFormDialog open={open} onOpenChange={setOpen} onSubmit={create} campaigns={campaigns} />
    </div>
  );
}

function SegmentCard({ icon: Icon, accent, label, value, sub, cta, onCta, testid }) {
  const styles = {
    emerald: { ring: "border-emerald-200", bg: "bg-emerald-50/40", chip: "bg-emerald-100 text-emerald-700", value: "text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700" },
    indigo: { ring: "border-indigo-200", bg: "bg-indigo-50/40", chip: "bg-indigo-100 text-indigo-700", value: "text-indigo-700", btn: "bg-indigo-600 hover:bg-indigo-700" },
    zinc:   { ring: "border-zinc-200",   bg: "bg-zinc-50/60",  chip: "bg-zinc-200 text-zinc-800", value: "text-zinc-900",  btn: "bg-zinc-900 hover:bg-zinc-800" },
  }[accent] || {};
  return (
    <div className={`relative bg-white border ${styles.ring} rounded-xl p-6 card-crisp ${styles.bg}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg ${styles.chip} flex items-center justify-center`}>
          <Icon className="w-5 h-5"/>
        </div>
        <span className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">Segment</span>
      </div>
      <div className="mt-4">
        <div className="text-xs uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
        <div className={`font-heading text-5xl font-black mt-1 ${styles.value}`}>{value}</div>
        <div className="text-sm text-zinc-500 mt-1">{sub}</div>
      </div>
      <button
        onClick={onCta}
        className={`mt-5 inline-flex items-center gap-1 text-white text-xs px-3 py-1.5 rounded-md ${styles.btn} btn-lift`}
      >
        {cta} <ArrowRight className="w-3 h-3"/>
      </button>
    </div>
  );
}

